import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Whether the observed element is currently clipping its own inline content —
 * i.e. whether a `truncate`d element is actually rendering its ellipsis right now.
 *
 * This can only be answered from layout (`scrollWidth > clientWidth`), never from
 * the markup: the same string overflows or not depending on the width the
 * surrounding flex/grid layout hands the element.
 *
 * `ref` is a CALLBACK ref, so the first measurement runs in the commit that
 * attaches the node — before paint, so a tooltip is never briefly enabled on text
 * that fits — and re-runs on every remount. A `ResizeObserver` then keeps it honest
 * while the element's box changes (window resize, a resized column, a panel
 * collapsing).
 *
 * A resize observer sees BOX changes only. New text at an unchanged box size
 * overflows differently and reports nothing, so callers whose content can change
 * in place pass it as `contentKey`.
 */
export const useIsTextOverflowing = (
  contentKey?: unknown,
): { ref: (node: HTMLElement | null) => void; isOverflowing: boolean } => {
  const [isOverflowing, setIsOverflowing] = useState(false)
  const nodeRef = useRef<HTMLElement | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  const measure = useCallback(() => {
    const node = nodeRef.current
    // 1px of slack: sub-pixel text advances round `scrollWidth` and `clientWidth`
    // apart on text that fits exactly, which a strict `>` reads as overflow and
    // arms a tooltip on an un-truncated cell.
    setIsOverflowing(node !== null && node.scrollWidth - node.clientWidth > 1)
  }, [])

  const ref = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      nodeRef.current = node
      measure()

      // jsdom (and any SSR pass that somehow reaches here) has no ResizeObserver;
      // the one-shot measurement above still stands, only the follow-up is lost.
      if (node === null || typeof ResizeObserver === 'undefined') {
        return
      }

      const observer = new ResizeObserver(measure)
      observer.observe(node)
      observerRef.current = observer
    },
    [measure],
  )

  useEffect(
    function remeasureOnContentChange() {
      measure()
    },
    [measure, contentKey],
  )

  return { ref, isOverflowing }
}
